import { useCallback } from 'react'
import { useAdpSessionStore } from './useAdpSessionStore'
import { useAppDispatch } from '@/app/reduxHooks'
import { markValidationDirty } from '@/features/adp/state/validationSlice'

export interface TransformationPayload {
  [key: string]: unknown
}

interface TransformationEntry {
  data: TransformationPayload
  createdAt: number
}

interface NodeTransformationWrapper {
  transformationData?: TransformationEntry
}

export function useTransformationData() {
  const { getNode, setNode, getAll } = useAdpSessionStore()
  const dispatch = useAppDispatch()

  /**
   * Set transformation data (overwrite)
   */
  const setTransformationData = useCallback(
    (nodeId: string, payload: TransformationPayload) => {
      const entry: TransformationEntry = {
        data: payload,
        createdAt: Date.now(),
      }

      setNode(nodeId, {
        transformationData: entry,
      })
      dispatch(markValidationDirty())
    },
    [dispatch, setNode]
  )

  /**
   * Update transformation data (merge). If no entry exists yet, creates
   * one so the write is never silently dropped.
   */
  const updateTransformationData = useCallback(
    (nodeId: string, payload: TransformationPayload) => {
      const existing = getNode(nodeId) as NodeTransformationWrapper | null

      const nextData: TransformationPayload = existing?.transformationData
        ? { ...existing.transformationData.data, ...payload }
        : { ...payload }

      setNode(nodeId, {
        transformationData: {
          data: nextData,
          createdAt: existing?.transformationData?.createdAt ?? Date.now(),
        },
      })
      dispatch(markValidationDirty())
    },
    [dispatch, getNode, setNode]
  )

  /**
   * Get all transformation data
   */
  const getAllTransformationData = useCallback((): TransformationEntry[] => {
    const all = getAll()

    return Object.values(all)
      .map(node => (node as NodeTransformationWrapper).transformationData)
      .filter((v): v is TransformationEntry => Boolean(v))
  }, [getAll])

  /**
   * Get transformation data by nodeId
   */
  const getTransformationDataByNodeId = useCallback(
    (nodeId: string): TransformationEntry | null => {
      const node = getNode(nodeId) as NodeTransformationWrapper | null
      return node?.transformationData ?? null
    },
    [getNode]
  )

  /**
   * Remove transformation data (optional but useful)
   */
  const clearTransformationData = useCallback(
    (nodeId: string) => {
      const existing = getNode(nodeId) as NodeTransformationWrapper | null
      if (!existing?.transformationData) return

      setNode(nodeId, {
        transformationData: undefined,
      })
      dispatch(markValidationDirty())
    },
    [dispatch, getNode, setNode]
  )

  return {
    setTransformationData,
    updateTransformationData,
    getAllTransformationData,
    getTransformationDataByNodeId,
    clearTransformationData,
  }
}
